import { z } from 'zod';

import { ColorPalette } from '@/entities/chart';
import { ItemSelection } from '@/features/dashboard/components/AppForm/AppForm/components/ItemSelection';
import { SelectChartType } from '@/features/dashboard/components/AppForm/AppForm/components/SelectChartType';
import { SelectSeasonalRange } from '@/features/dashboard/components/AppForm/AppForm/components/SelectSeasonalRange';
import { TabName } from '@/features/dashboard/components/AppForm/AppForm/components/TabName';
import type { ChartFormValues } from '@/features/dashboard/components/AppForm/AppForm/context/types';
import { DrawerStep } from '@/features/dashboard/components/Drawer/types';
import { SEASONAL_CHART_MAX_ITEMS } from '@/features/dashboard/constants/const';
import type { SeasonalChartWidgetV2 } from '@/features/dashboard/types';
import { AppType } from '@/features/dashboard/types';

import type { FormAppDefinition } from '../types';
import {
  buildChart,
  ColorPaletteSchema,
  graphToFormValues,
  NameSchema,
  SymbolsStateSchema
} from './_helpers';

export const seasonalChartDefinition: FormAppDefinition<ChartFormValues, SeasonalChartWidgetV2> = {
  type: AppType.Seasonal,
  drawerStep: DrawerStep.SelectSpotChart,
  dashboardControls: { edit: true },
  widgetControls: { export: true },
  maxItems: SEASONAL_CHART_MAX_ITEMS,
  initialValues: {
    name: 'Spot History chart',
    type: AppType.Seasonal,
    symbols: { active: [], backlog: [] },
    colorPalette: ColorPalette.Vibrant
  },
  schema: z.object({
    id: z.string().optional(),
    type: z.literal(AppType.Seasonal),
    name: NameSchema,
    symbols: SymbolsStateSchema,
    colorPalette: ColorPaletteSchema,
    from: z.string().optional()
  }),
  heading: { title: 'Configure spot chart', subtitle: 'Set up your chart preferences and data' },
  Form: () => (
    <div className='flex flex-col gap-6'>
      <TabName />
      <SelectChartType />
      <SelectSeasonalRange />
      <ItemSelection />
    </div>
  ),
  toWidget: values => buildChart(values),
  canEditWidget: (w): w is SeasonalChartWidgetV2 =>
    w.type === 'graph' && w.metadata?.graphType === 'seasonal',
  fromWidget: w => graphToFormValues(w)
};
